import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import DateFields, { chartDateHref, dateFromSearch, dateParam, emptyDate, parseDate } from '../../components/DateFields/DateFields';
import { noteAstrology } from '../../services/chartService';
import { birthCardsFor } from '../../data/astro/birthCards';
import '../Astro/Astro.css';
import './BirthCard.css';

const FAQ = [
  {
    question: 'What is a tarot birth card?',
    answer: 'A tarot birth card is a Major Arcana card found from your date of birth. Most people get a pair: a soul card that describes your deeper purpose and a personality card that describes how you show up day to day. The method used here was popularized by tarot author Mary K. Greer.',
  },
  {
    question: 'How is the tarot birth card calculated?',
    answer: 'Add your birth month, day, and full year as numbers, then add the digits of the total. Keep adding digits until the result is 22 or less. A result from 10 to 21 gives two cards: that card and the card of its digit sum. A result of 19 gives three cards, and 22 gives The Fool with The Emperor.',
  },
  {
    question: 'What is the difference between the soul card and the personality card?',
    answer: 'The personality card is the higher, two-digit card and describes your everyday nature and how others experience you. The soul card is the single-digit card it reduces to, read as the core of who you are across your whole life. When you get a single card, it plays both roles.',
  },
  {
    question: 'Can my birth card change?',
    answer: 'No. It comes from your birthday, so it stays with you for life. Many people find their birth cards turn up in their readings more often than chance would suggest.',
  },
];

const BirthCard = () => {
  const location = useLocation();
  const [date, setDate] = useState(() => dateFromSearch(location.search) || emptyDate());
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const resultRef = useRef(null);

  const calculate = (value, scroll = true) => {
    const parsed = parseDate(value);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError('');
    const cards = birthCardsFor(parsed.year, parsed.month, parsed.day);
    const born = `${parsed.year}-${String(parsed.month).padStart(2, '0')}-${String(parsed.day).padStart(2, '0')}`;
    setResult({ ...cards, date: parsed });
    noteAstrology('birth-card', cards.cards.map((card) => card.name).join(', '), born);
    if (scroll) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  };

  useEffect(() => {
    const fromUrl = dateFromSearch(location.search);
    if (fromUrl) calculate(fromUrl, false);
    // Only the first render reads the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    calculate(date);
  };

  const steps = result ? [
    `${result.date.month} + ${result.date.day} + ${result.date.year} = ${result.total}`,
    ...result.steps.slice(1).map((step, index) => `${String(result.steps[index]).split('').join(' + ')} = ${step}`),
  ] : [];
  const names = result ? result.cards.map((card) => card.name) : [];
  const roleFor = (index) => {
    if (!result || result.cards.length === 1) return 'Soul and personality card';
    return index === 0 ? 'Personality card' : 'Soul card';
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Tarot Birth Card Calculator',
        url: 'https://nowtarot.com/tarot-birth-card-calculator/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Free tarot birth card calculator. Find your soul card and personality card from your birthday.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <div className="astro-page">
      <Helmet>
        <title>Tarot Birth Card Calculator: Find Your Soul Card | NowTarot</title>
        <meta name="description" content="Free tarot birth card calculator. Enter your birthday to find your Major Arcana soul card and personality card, with the full meaning of your pair." />
        <link rel="canonical" href="https://nowtarot.com/tarot-birth-card-calculator/" />
        <meta property="og:title" content="Tarot Birth Card Calculator: Find Your Soul Card" />
        <meta property="og:description" content="Your birthday, read through the Major Arcana: your soul card and personality card." />
        <meta property="og:url" content="https://nowtarot.com/tarot-birth-card-calculator/" />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <p className="astro-eyebrow">Tool · Free · Tarot and numerology</p>
        <h1>Your tarot <em>birth card</em></h1>
        <p className="astro-lede">
          Your birthday points to one, two, or three cards of the Major Arcana: a soul card for who you are underneath,
          and a personality card for how you move through the world.
        </p>

        <section className="astro-card">
          <form className="astro-form" onSubmit={handleSubmit}>
            <DateFields value={date} onChange={(next) => { setDate(next); setError(''); }} />
            <button className="astro-button" type="submit">Find my cards</button>
          </form>
          {error && <p className="astro-error">{error}</p>}
        </section>

        {result && (
          <section className="astro-card" ref={resultRef} aria-live="polite">
            <p className="astro-eyebrow">Your tarot birth {result.cards.length === 1 ? 'card' : 'cards'}</p>
            <h2>{names.join(result.cards.length === 2 ? ' and ' : ', ')}</h2>
            <div className="astro-steps">{steps.join('\n')}</div>
            <div className={`birthcard-cards count-${result.cards.length}`}>
              {result.cards.map((card, index) => (
                <figure key={card.slug}>
                  <img src={card.image} alt={card.name} />
                  <figcaption>
                    <span className="astro-eyebrow">{roleFor(index)}</span>
                    <strong>{card.name}</strong>
                    <span>{card.line}</span>
                    <a href={`/tarot-card-meanings/${card.slug}/`}>Full {card.name} meaning →</a>
                  </figcaption>
                </figure>
              ))}
            </div>
            {result.pairText && (
              <>
                <h3>What your cards mean together</h3>
                <p>{result.pairText}</p>
              </>
            )}
            {result.cards.length === 1 && result.family.length > 0 && (
              <p>
                {result.cards[0].name} also belongs to a family of cards that reduce to {result.numbers[0]}:{' '}
                {result.family.map((card) => `${card.name} (${card.number})`).join(' and ')}. Many people with this birth card
                notice those cards turning up for them too.
              </p>
            )}
            <ShareRow
              url="/tarot-birth-card-calculator/"
              text={`My tarot birth ${result.cards.length === 1 ? 'card is' : 'cards are'} ${names.join(' and ')}. Find yours:`}
            />
          </section>
        )}

        {result && (
          <section className="astro-section">
            <h2>Keep going</h2>
            <ul className="astro-next">
              <li className="astro-next-lead">
                <a href={chartDateHref(result.date)}>
                  <strong>Your birth chart</strong>
                  <span>Add your birth time and place for every planet, the houses, and a reading you can ask about.</span>
                </a>
              </li>
              <li>
                <a href={`/life-path-number-calculator/?date=${dateParam(result.date)}`}>
                  <strong>Your life path number</strong>
                  <span>The same birthday through numerology.</span>
                </a>
              </li>
              <li>
                <a href="/free-tarot/"><strong>Free tarot reading</strong><span>Shuffle the full deck and see if your birth card turns up.</span></a>
              </li>
            </ul>
          </section>
        )}

        <section className="astro-section">
          <h2>How tarot birth cards work</h2>
          <p>
            The 22 cards of the Major Arcana tell the story of a life, from The Fool's first step to The World's
            completion. Your birthday reduces to one of those cards, and usually to a pair that shares a root number,
            like Strength (8) and The Star (17). The higher card describes how you meet the world. The lower one
            describes the soul underneath it.
          </p>
          <p>
            Birth cards are not a prediction. They are a lens: a way to notice the themes you keep returning to, and a
            pair of cards to sit with when you want to understand yourself a little better.
          </p>
        </section>

        <section className="astro-section">
          <h2>Tarot birth card FAQ</h2>
          <div className="astro-faq">
            {FAQ.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BirthCard;
